import React, { useState } from 'react';
import svgPaths from "./imports/svg-s21rrkv6a1";
import svgPathsFrame1321 from "./imports/svg-fn293padn3";
import clsx from "clsx";
import { CreateNewBrandKitModal } from './components/CreateNewBrandKitModal';
import { useNavigate } from 'react-router-dom';
import { useGetBrandKitsQuery } from '../../features/brand-kit/brandKitApi';

type Wrapper1Props = {
  additionalClassNames?: string;
};

function Wrapper1({ children, additionalClassNames = "" }: React.PropsWithChildren<Wrapper1Props>) {
  return (
    <div className={additionalClassNames}>
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 16 16">
        {children}
      </svg>
    </div>
  );
}

function Wrapper({ children }: React.PropsWithChildren) {
  return (
    <div className="relative shrink-0 size-[20px]">
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 20 20">
        {children}
      </svg>
    </div>
  );
}
type TextProps = {
  text: string;
  additionalClassNames?: string;
};

function Text({ text, additionalClassNames = "" }: TextProps) {
  return (
    <div className={clsx("content-stretch flex gap-[8px] items-center relative shrink-0", additionalClassNames)}>
      <Wrapper>
        <g id="Frame">
          <path d={svgPaths.p32ddfd00} id="Vector" stroke="var(--stroke-0, #1D2739)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
        </g>
      </Wrapper>
      <p className="font-sans font-normal leading-[24px] relative shrink-0 text-[#1d2739] text-[14px] text-nowrap tracking-[0.14px]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        {text}
      </p>
    </div>
  );
}

export default function BrandKitPage() {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset to page 1 on search
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useGetBrandKitsQuery({
    page,
    limit: 20,
    search: debouncedSearch
  });

  const brandKits = data?.data || [];

  return (
    <>
      <CreateNewBrandKitModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        mode="create"
      />
      <div className="flex-1 overflow-y-auto w-full custom-scrollbar pb-10">
        <div className="bg-white h-[131px] relative rounded-[8px] shrink-0 w-full overflow-hidden">
          <div className="overflow-clip relative rounded-[inherit] size-full">
            <div className="absolute flex h-[234.057px] items-center justify-center left-[199.11px] top-[-15.61px] w-[136.499px]" style={{ "--transform-inner-width": "300", "--transform-inner-height": "150" } as React.CSSProperties}>
              <div className="flex-none rotate-[23.797deg]">
                <div className="h-[235.892px] relative w-[45.157px]">
                  <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 46 236">
                    <path d={svgPaths.p2a04de00} fill="var(--fill-0, white)" id="Rectangle 34624130" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="absolute h-[385px] left-[-42px] top-[-271px] w-[513px]">
              <div className="absolute inset-[-129.87%_-97.47%]">
                <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 1513 1385">
                  <g filter="url(#filter0_f_58_750)" id="Ellipse 20">
                    <ellipse cx="756.5" cy="692.5" fill="var(--fill-0, #F9D495)" rx="256.5" ry="192.5" />
                  </g>
                  <defs>
                    <filter colorInterpolationFilters="sRGB" filterUnits="userSpaceOnUse" height="1385" id="filter0_f_58_750" width="1513" x="0" y="0">
                      <feFlood floodOpacity="0" result="BackgroundImageFix" />
                      <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
                      <feGaussianBlur result="effect1_foregroundBlur_58_750" stdDeviation="250" />
                    </filter>
                  </defs>
                </svg>
              </div>
            </div>
            <div className="absolute h-[369px] right-0 top-[-224px] w-[573px]">
              <div className="absolute inset-[-135.5%_-87.26%]">
                <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 1573 1369">
                  <g filter="url(#filter0_f_112_1398)" id="Ellipse 21">
                    <ellipse cx="786.5" cy="684.5" fill="var(--fill-0, #BEA4F3)" rx="286.5" ry="184.5" />
                  </g>
                  <defs>
                    <filter colorInterpolationFilters="sRGB" filterUnits="userSpaceOnUse" height="1369" id="filter0_f_112_1398" width="1573" x="0" y="0">
                      <feFlood floodOpacity="0" result="BackgroundImageFix" />
                      <feBlend in="SourceGraphic" in2="BackgroundImageFix" mode="normal" result="shape" />
                      <feGaussianBlur result="effect1_foregroundBlur_112_1398" stdDeviation="250" />
                    </filter>
                  </defs>
                </svg>
              </div>
            </div>
            <div className="absolute content-stretch flex flex-col gap-[16px] items-start left-[24px] top-[calc(50%+0.5px)] translate-y-[-50%]">
              <p className="font-sans font-bold leading-[normal] min-w-full relative shrink-0 text-[#101928] text-[24px] w-[min-content]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                One Kit. Endless On-Brand Designs.
              </p>
              <div className="content-stretch flex flex-col gap-[16px] items-start relative shrink-0 w-[536px]">
                <Text text="Add your logo, colors, and fonts once — your Brand Kit remembers it all." />
                <Text text="Save your designs as Brand Templates to easily reuse with your entire team" additionalClassNames="w-full" />
              </div>
            </div>
            <div className="absolute left-[836px] size-[100px] top-[56px]" />
          </div>
          <div aria-hidden="true" className="absolute border border-[#e4e7ec] border-solid inset-0 pointer-events-none rounded-[8px]" />
        </div>

        <div className="content-stretch flex flex-col gap-[16px] items-start w-full mt-6">
          <div className="content-stretch flex items-center justify-between relative shrink-0 w-full">
            <div className="bg-white h-[48px] relative rounded-[12px] shrink-0 w-[448px]" data-name="Search bar">
              <div className="content-stretch flex items-center overflow-clip px-[12px] py-[3px] relative rounded-[inherit] size-full">
                <div className="content-stretch flex items-center relative shrink-0 w-full">
                  <div className="content-stretch flex gap-[8px] items-center relative shrink-0 w-full">
                    <div className="relative shrink-0 size-[16px]" data-name="search-normal">
                      <Wrapper1 additionalClassNames="absolute contents inset-0">
                        <g id="search-normal">
                          <path d={svgPaths.p20103e00} id="Vector" stroke="var(--stroke-0, #98A2B3)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                          <path d={svgPaths.p133f8470} id="Vector_2" stroke="var(--stroke-0, #98A2B3)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.25" />
                          <g id="Vector_3" opacity="0"></g>
                        </g>
                      </Wrapper1>
                    </div>
                    <input
                      type="text"
                      placeholder="Search brand kit name..."
                      className="font-sans font-normal leading-[24px] relative flex-1 text-[#101928] text-[14px] bg-transparent outline-none px-2"
                      style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div aria-hidden="true" className="absolute border-[#e4e7ec] border-[0.75px] border-solid inset-0 pointer-events-none rounded-[12px]" />
            </div>
            <div
              className="bg-[#30a84b] content-stretch flex gap-[4px] h-[36px] items-center justify-center p-[12px] relative rounded-[6px] shrink-0 cursor-pointer hover:bg-[#288e3e] transition-colors"
              data-name="Primary - Sm"
              onClick={() => setIsModalOpen(true)}
            >
              <div className="content-stretch flex gap-[4px] items-center relative shrink-0">
                <div className="relative shrink-0 size-[16px]" data-name="add">
                  <Wrapper1 additionalClassNames="absolute contents inset-0">
                    <g id="add">
                      <path d="M4 8H12" id="Vector" stroke="var(--stroke-0, white)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                      <path d="M8 12V4" id="Vector_2" stroke="var(--stroke-0, white)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                      <g id="Vector_3" opacity="0"></g>
                    </g>
                  </Wrapper1>
                </div>
                <p className="font-sans font-medium leading-[normal] relative shrink-0 text-[14px] text-nowrap text-white" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                  New Brand Kit
                </p>
              </div>
            </div>
          </div>

          {/* Brand Kit Grid */}
          <div className="content-stretch flex flex-col gap-[16px] items-start relative shrink-0 w-full pb-8">
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white h-[81px] rounded-[8px] animate-pulse relative border border-gray-100">
                    <div className="absolute inset-0 flex items-center px-4">
                      <div className="h-6 w-32 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : brandKits.length === 0 ? (
              <div className="w-full flex flex-col items-center justify-center h-40 bg-white rounded-lg border border-dashed border-gray-200">
                <p className="text-gray-500 font-medium">No brand kits found</p>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="mt-2 text-[#30a84b] font-medium hover:underline"
                >
                  Create your first Brand Kit
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-[16px] w-full">
                {brandKits.map((kit: any) => (
                  <div key={kit._id || kit.id} className="bg-white relative rounded-[8px] shrink-0 w-full cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/brand-kit/edit/${kit._id || kit.id}`)}>
                    <div className="content-stretch flex items-center justify-between overflow-clip px-[16px] py-[20px] relative rounded-[inherit] w-full">
                      <div className="content-stretch flex flex-col items-start relative shrink-0">
                        <div className="content-stretch flex flex-col items-start relative shrink-0">
                          <div className="content-stretch flex gap-[12px] items-center relative shrink-0">
                            <div className="flex flex-col font-sans font-bold justify-center leading-[0] relative shrink-0 text-[#101928] text-[20px] text-nowrap" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                              <p className="leading-[normal]">{kit.name}</p>
                            </div>
                            {kit.category && (
                              <div className="bg-gradient-to-b content-stretch flex from-[rgba(11,162,155,0.08)] items-center justify-center p-[6px] relative rounded-[4px] shrink-0 to-[rgba(67,180,141,0.08)]">
                                <div className="flex flex-col font-sans font-medium justify-center leading-[0] relative shrink-0 text-[#227735] text-[12px] text-nowrap" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
                                  <p className="leading-[normal]">{kit.category}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="content-stretch flex gap-[32px] items-center justify-end relative shrink-0">
                        <div className="relative shrink-0 size-[20px] cursor-pointer hover:opacity-70 transition-opacity z-10" onClick={(e) => { e.stopPropagation(); navigate(`/brand-kit/edit/${kit._id || kit.id}`); }}>
                          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 20 20">
                            <g clipPath="url(#clip0_114_2267)" id="Frame">
                              <path d={svgPathsFrame1321.p30d9cf00} id="Vector" stroke="var(--stroke-0, #344054)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                              <path d="M12.5 4.16667L15.8333 7.5" id="Vector_2" stroke="var(--stroke-0, #344054)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                            </g>
                            <defs>
                              <clipPath id="clip0_114_2267">
                                <rect fill="white" height="20" width="20" />
                              </clipPath>
                            </defs>
                          </svg>
                        </div>
                        <div className="relative shrink-0 size-[20px] cursor-pointer hover:opacity-70 transition-opacity z-10" data-name="Delete" onClick={(e) => { e.stopPropagation(); /* Delete logic to be implemented */ }}>
                          <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 20 20">
                            <g id="trash-01">
                              <path d={svgPathsFrame1321.p12f45a80} id="Icon" stroke="var(--stroke-0, #F04438)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
                            </g>
                          </svg>
                        </div>
                      </div>
                    </div>
                    <div aria-hidden="true" className="absolute border border-[#e4e7ec] border-solid inset-0 pointer-events-none rounded-[8px]" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

